import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import CaseStudies from './CaseStudies';
import Testimonials from './Testimonials';
import ResourcesSection from './ResourcesSection';
import ContactForm from './ContactForm';
import HeroCarousel from './HeroCarousel';
import { ArrowRight } from 'lucide-react';

interface HomePageProps {
  scrollToSection: (id: string) => void;
  onFormStart?: () => void;
  onFormComplete?: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ scrollToSection, onFormStart, onFormComplete }) => {
  const { t } = useTranslation();

  return (
    <div>
      <HeroCarousel>
        <div className="container mx-auto px-4 h-full flex items-center justify-center">
          <div className="max-w-4xl mx-auto text-center py-16">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight">
              {t('hero.title')}
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-white font-bold mb-6">
              Schedule a consultation to discuss how we can elevate your practice's patient education and marketing
            </p>
            <p className="text-lg sm:text-xl text-white font-semibold mb-12 bg-white/20 inline-block px-6 py-2 rounded-full backdrop-blur-sm">
              {t('hero.languages')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-16">
              <button
                onClick={() => scrollToSection('services')}
                className="bg-white text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-md text-base font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center justify-center"
              >
                {t('hero.exploreServices')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="bg-indigo-500 text-white hover:bg-indigo-600 px-6 py-3 rounded-md text-base font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-lg border-2 border-white/20 flex items-center justify-center"
              >
                {t('hero.scheduleDemo')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </HeroCarousel>

      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('services.title')}
            </h2>
            <h3 className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('services.description')}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Custom Video Content",
                description: "Engage patients with clear, professional videos explaining procedures, conditions, and aftercare instructions.",
                features: ["Professional narration", "Custom branding", "Multi-language options"]
              },
              {
                title: "Digital Patient Journey",
                description: "Guide patients through their healthcare experience with personalized education and support materials.",
                features: ["Custom pathways", "Progress tracking", "Automated follow-up"]
              },
              {
                title: "Marketing Automation",
                description: "Attract and retain patients with targeted campaigns and engagement strategies.",
                features: ["Email sequences", "Social media", "Review management"]
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col h-full">
                <div className="p-6 flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-8">{service.description}</p>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Key Features:</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-gray-600">
                          <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="p-6 border-t border-gray-100">
                  <button 
                    onClick={() => scrollToSection('contact')}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                  >
                    Learn More
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="case-studies" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Use Case Examples</h2>
            <h3 className="text-lg text-gray-600 max-w-3xl mx-auto">See how our solutions transform patient education and practice growth across different specialties.</h3>
          </div>
          <CaseStudies />
        </div>
      </section>

      <section id="resources" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Free Healthcare Resources</h2>
            <h3 className="text-lg text-gray-600 max-w-3xl mx-auto">Download our comprehensive guides, templates, and whitepapers to enhance your healthcare practice.</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Patient Education Guide",
                description: "Comprehensive guide for implementing effective patient education programs.",
                icon: "📚",
                formats: ["PDF", "DOC", "MD"]
              },
              {
                title: "Content Calendar Template",
                description: "12-month content planning template with healthcare-specific categories.",
                icon: "📅",
                formats: ["DOC", "PDF"]
              },
              {
                title: "ROI Whitepaper",
                description: "Research-backed analysis of patient education program ROI.",
                icon: "📊",
                formats: ["PDF", "MD"]
              }
            ].map((resource, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
                <div className="text-4xl mb-4">{resource.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{resource.title}</h3>
                <p className="text-gray-600 mb-4">{resource.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    {resource.formats.map((format, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {format}
                      </span>
                    ))}
                  </div>
                  <Link 
                    to="/resources" 
                    className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                  >
                    Download
                    <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/resources"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              View All Resources
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Get Started Today</h2>
              <h3 className="text-lg text-gray-600 max-w-3xl mx-auto">Schedule a consultation to discuss how we can elevate your practice's patient education and marketing.</h3>
            </div>
            <ContactForm
              onFormStart={onFormStart}
              onFormComplete={onFormComplete}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;